import React from 'react'
import {
    ChatFieldsFragment,
    ChatMessageFieldsFragment,
} from '~graphql/generated/graphql'
import UserMessage from './user-message'
import MessageSpacing from './message-spacing'
import SystemMessage from './system-message'
import MessagePropertiesProvider, {
    useMessageProperties,
} from './message-provider'
import MessageErrorPreview from './message-error-preview'
import { useChat } from '~components/chats/chat/chat-context'
import { Box, Tag, Tooltip } from '@chakra-ui/react'
import { stringToColor } from '~utils/helpers'

interface Props {
    message: ChatMessageFieldsFragment
    messageIndex: number
    allMessages: ChatMessageFieldsFragment[]
    chat: ChatFieldsFragment
    refetchMessages: () => void
    showLocationTag?: boolean
}

const MessageContent: React.FC<Props> = ({
    message,
    messageIndex,
    allMessages,
    refetchMessages,
    chat,
    showLocationTag,
}) => {
    return (
        <MessagePropertiesProvider
            message={message}
            messageIndex={messageIndex}
            allMessages={allMessages}
            chat={chat}
            refetchMessages={refetchMessages}
        >
            <MessageInnerContent showLocationTag={showLocationTag} />
        </MessagePropertiesProvider>
    )
}

function MessageInnerContent({
    showLocationTag,
}: {
    showLocationTag?: boolean
}) {
    const { message, isSystemMsg } = useMessageProperties()
    const { showErrors } = useChat()

    const hasErrors =
        !!message.email_error ||
        !!message.sms_error ||
        !!message.postcard_error ||
        !!message.other_errors?.length

    if (hasErrors) {
        if (!showErrors) return null

        return (
            <React.Fragment key={message.id}>
                <MessageSpacing />
                <MessageErrorPreview showLocationTag={showLocationTag} />
            </React.Fragment>
        )
    }

    return (
        <React.Fragment key={message.id}>
            <MessageSpacing />
            {isSystemMsg ? (
                <SystemMessage showLocationTag={showLocationTag} />
            ) : (
                <UserMessage showLocationTag={showLocationTag} />
            )}
        </React.Fragment>
    )
}

export function MessageLocationTag() {
    const { message } = useMessageProperties()
    const clinicName = message.chat.clinic.name || 'Unknown location'

    return (
        <Tooltip label={`This message was sent from ${clinicName}`}>
            <Tag size="xs" colorScheme={stringToColor(clinicName)}>
                {clinicName}
            </Tag>
        </Tooltip>
    )
}

export default MessageContent
